import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareText, ChevronLeft, UserPlus, Loader2 } from "lucide-react";
import { RequestAccessDialog } from "@/pages/CommunitySite";
import { useCommunityBrand } from "@/hooks/useCommunityBrand";
import { rememberedPortalBrand } from "./portalApi";
import { useBrandTheme } from "@/lib/brand";
import { ResidentDoor } from "@/components/ResidentDoor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One screen: email or mobile → password (or a one-time activation), then signed in for good.
export default function PortalLogin() {
  const [params] = useSearchParams();
  const backTo = (params.get("from") || "").startsWith("/c/") ? params.get("from") : "";
  const slug = backTo ? (backTo.replace(/^\/c\//, "").split(/[/?#]/)[0] || "") : "";
  const doorBrand = useCommunityBrand(slug);
  const remembered = useMemo(() => (slug ? null : rememberedPortalBrand()), [slug]);
  const brand = doorBrand || remembered;
  const accent = (brand && brand.brand_color) || "";
  useBrandTheme(accent);
  const logoUrl = brand && brand.has_logo
    ? (slug ? `${API}/public/site/${slug}/logo` : (brand.tenant_id ? `${API}/public/tenant/${brand.tenant_id}/logo` : ""))
    : "";

  const [ident, setIdent] = useState(params.get("prefill") || "");
  const [step, setStep] = useState("identify"); // identify | resident | account | pending | staff | unknown
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sites, setSites] = useState(null);
  const [raSlug, setRaSlug] = useState("");
  const reset = () => { setStep("identify"); setLookup(null); };

  const check = async (e) => {
    e?.preventDefault();
    if (!ident.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/lookup`, { identifier: ident.trim() });
      setLookup(data);
      if (data.kind === "resident") setStep("resident");
      else if (data.kind === "pending") setStep("pending");
      else if (data.kind === "staff" || data.kind === "both") setStep("staff");
      else setStep("unknown");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't check that right now."); }
    finally { setBusy(false); }
  };

  const openRequestAccess = async () => {
    let list = sites;
    if (list === null) {
      try { const r = await axios.get(`${API}/public/sites`); list = r.data.items || []; setSites(list); }
      catch { list = []; setSites([]); }
    }
    if (list.length === 0) { toast.info("Ask your community manager to add your phone number — you'll be able to sign in right away."); return; }
    setRaSlug(list.length === 1 ? list[0].slug : "pick");
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#1a1614] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md min-w-0">
        {backTo && (
          <Link to={backTo} data-testid="portal-login-back" className="inline-flex items-center gap-1.5 text-sand-400 hover:text-white text-sm font-semibold mb-5 transition-colors">
            <ChevronLeft className="h-4 w-4" /> Back to community page
          </Link>
        )}
        <div className="text-center mb-8">
          {brand ? (
            <div data-testid="portal-community-brand">
              {logoUrl && <img src={logoUrl} alt={brand.community} data-testid="portal-login-logo" className="h-14 mx-auto mb-3 rounded-2xl bg-white p-2 object-contain" />}
              <div className="font-heading font-extrabold text-3xl text-white">{brand.community}</div>
              <p className="text-sand-400 text-sm mt-2">Resident sign in · Powered by Ask Redline</p>
            </div>
          ) : (
            <>
              <div className="font-heading font-extrabold text-3xl text-white">Ask <span className="text-[#D32F2F]">Redline</span></div>
              <p className="text-sand-400 text-sm mt-2">Your community, one text away.</p>
            </>
          )}
        </div>
        <div className="bg-white rounded-3xl p-6 sm:p-7 shadow-2xl" data-testid="portal-login-card">
          {step === "identify" && (
            <form onSubmit={check} data-testid="portal-identify-form">
              <h1 className="font-heading font-bold text-xl text-ink">Resident sign in</h1>
              <p className="text-sm text-ink/60 mt-1">Enter the mobile number or email your community has on file.</p>
              <input data-testid="portal-identifier" autoFocus value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="(555) 123-4567 or you@example.com"
                className="mt-5 w-full rounded-2xl border border-sand-200 px-4 py-3 text-base font-semibold outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button data-testid="portal-continue" type="submit" disabled={busy || !ident.trim()} style={accent ? { backgroundColor: accent } : undefined}
                className="mt-4 w-full rounded-full bg-redline-500 text-white font-bold py-3.5 hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> One moment…</> : "Continue"}
              </button>
              <button type="button" data-testid="portal-request-access" onClick={openRequestAccess}
                className="mt-3 w-full text-sm font-semibold text-ink/60 hover:text-ink flex items-center justify-center gap-1.5">
                <UserPlus className="h-4 w-4" /> Not set up yet? Request access
              </button>
            </form>
          )}
          {step === "resident" && <ResidentDoor identifier={ident.trim()} lookup={lookup} accent={accent} onBack={reset} testPrefix="portal-res" />}
          {step === "account" && <ResidentDoor identifier={ident.trim()} lookup={{}} initialStep="account" accent={accent} onBack={reset} testPrefix="portal-res" />}
          {step === "pending" && (
            <div data-testid="portal-pending">
              <h1 className="font-heading font-bold text-xl text-ink">Almost there</h1>
              <p className="text-sm text-ink/60 mt-1">You've registered, and the office still needs to confirm you live in the community. You'll be able to set a password as soon as they do.</p>
              <button onClick={reset} className="mt-4 text-sm font-semibold text-redline-600">Try a different number or email</button>
            </div>
          )}
          {step === "staff" && (
            <div data-testid="portal-staff">
              <h1 className="font-heading font-bold text-xl text-ink">That's a team account</h1>
              <p className="text-sm text-ink/60 mt-1">Managers and board members sign in to the console instead.</p>
              <a href="/login" data-testid="portal-go-staff" className="mt-4 block text-center w-full rounded-full bg-ink text-white font-bold py-3.5">Go to the team sign-in</a>
              <button onClick={reset} className="mt-3 w-full text-sm font-semibold text-ink/60 hover:text-ink">Use a different email or number</button>
            </div>
          )}
          {step === "unknown" && (
            <div data-testid="portal-unknown">
              <h1 className="font-heading font-bold text-xl text-ink">We don't have that on file</h1>
              <p className="text-sm text-ink/60 mt-1">Try the number or email your community has for you. If yours isn't on file yet, activate with the account number on your statement, or ask the office to add you.</p>
              <button data-testid="portal-use-account" onClick={() => setStep("account")} style={accent ? { backgroundColor: accent } : undefined}
                className="mt-4 w-full rounded-full bg-redline-500 text-white font-bold py-3.5 hover:opacity-90">Use my account number</button>
              <button onClick={openRequestAccess} className="mt-3 w-full text-sm font-semibold text-ink/60 hover:text-ink flex items-center justify-center gap-1.5"><UserPlus className="h-4 w-4" /> Request access</button>
              <button onClick={reset} className="mt-2 w-full text-sm font-semibold text-ink/60 hover:text-ink">Try another</button>
            </div>
          )}
        </div>
        <p className="text-center text-sand-500 text-xs mt-6 flex items-center justify-center gap-1.5">
          <MessageSquareText className="h-3.5 w-3.5" /> Prefer texting? Just text your community&apos;s concierge number — no login needed.
        </p>
      </div>

      {raSlug === "pick" && (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4" data-testid="ra-community-picker" onClick={() => setRaSlug("")}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading font-bold text-lg text-ink">Which community?</h2>
            <div className="mt-3 space-y-1.5">
              {(sites || []).map((s) => (
                <button key={s.slug} data-testid={`ra-pick-${s.slug}`} onClick={() => setRaSlug(s.slug)}
                  className="w-full text-left rounded-xl border border-sand-200 px-4 py-3 text-sm font-semibold text-ink hover:border-ink transition-colors">{s.community}</button>
              ))}
            </div>
          </div>
        </div>
      )}
      {raSlug && raSlug !== "pick" && (
        <RequestAccessDialog slug={raSlug} community={(sites || []).find((s) => s.slug === raSlug)?.community || "your community"}
          open onClose={() => setRaSlug("")} />
      )}
    </div>
  );
}
